import React, { useState, useRef, useEffect } from 'react';
import { Upload, RotateCcw, RotateCw, Download, Loader2, FileText, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { PDFDocument, degrees } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs`;

const FileRotator: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [rotation, setRotation] = useState(0); 
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setRotation(0);
    const isPdfFile = selectedFile.type === 'application/pdf';
    setIsPdf(isPdfFile);

    if (isPdfFile) {
      const reader = new FileReader();
      reader.onload = async () => {
        const typedarray = new Uint8Array(reader.result as ArrayBuffer);
        const pdf = await pdfjsLib.getDocument(typedarray).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.5 });
        
        const canvas = canvasRef.current;
        if (canvas) {
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          if (context) {
            await page.render({ canvasContext: context, viewport: viewport }).promise;
            setPreviewUrl(canvas.toDataURL());
          }
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    } else {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  };

  const rotate = (dir: 'left' | 'right') => {
    if (dir === 'left') {
      setRotation((prev) => (prev - 90 + 360) % 360);
    } else {
      setRotation((prev) => (prev + 90) % 360);
    }
  };

  const handleDownload = async () => {
    if (!file) return;
    setIsProcessing(true);

    try {
      if (isPdf) {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pages = pdfDoc.getPages();
        
        pages.forEach((page) => {
          const currentRotation = page.getRotation().angle;
          page.setRotation(degrees((currentRotation + rotation) % 360));
        });

        const pdfBytes = await pdfDoc.save();
        downloadBlob(new Blob([pdfBytes], { type: 'application/pdf' }), `rotated_${file.name}`);
      } else {
        const img = new Image();
        img.src = previewUrl!;
        await img.decode();

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const isHorizontal = rotation === 90 || rotation === 270;
        canvas.width = isHorizontal ? img.height : img.width;
        canvas.height = isHorizontal ? img.width : img.height;

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob((blob) => {
          if (blob) downloadBlob(blob, file.type);
        }, file.type);
      }
    } catch (error) {
      console.error('Rotation failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-8 max-w-2xl mx-auto shadow-sm">
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-zinc-900">File Rotator</h2>
        <p className="text-zinc-500 text-sm">
          Upload a PDF or Image to adjust its orientation. For PDFs, all pages will be rotated simultaneously.
        </p>
      </div>

      <div className="space-y-8">
        {!file ? (
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-zinc-200 hover:border-[#D4AF37] bg-zinc-50/50 rounded-lg p-12 flex flex-col items-center justify-center cursor-pointer transition-all group"
          >
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept=".pdf,.png,.jpg,.jpeg" 
              className="hidden" 
            />
            <Upload className="text-zinc-400 group-hover:text-[#D4AF37] w-12 h-12 mb-4 transition-colors" />
            <p className="text-zinc-500 font-medium">Select PDF or Image</p>
            <p className="text-zinc-400 text-xs mt-2">Supports .pdf, .jpg, .png</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="relative bg-zinc-50 rounded-lg border border-zinc-200 p-4 min-h-[300px] flex items-center justify-center overflow-hidden">
              {previewUrl ? (
                <div 
                  className="preview-rotate shadow-xl"
                  style={{ transform: `rotate(${rotation}deg)` }}
                >
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="max-h-[400px] max-w-full rounded border border-zinc-200"
                  />
                </div>
              ) : (
                <Loader2 className="animate-spin text-zinc-400" />
              )}
              
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full border border-zinc-200 flex items-center gap-2">
                {isPdf ? <FileText size={14} className="text-[#D4AF37]" /> : <ImageIcon size={14} className="text-[#D4AF37]" />}
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{file.name}</span>
              </div>
            </div>

            <canvas ref={canvasRef} className="hidden" />

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => rotate('left')}
                className="flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-semibold py-3 rounded-lg transition-colors border border-zinc-200"
              >
                <RotateCcw size={18} />
                Rotate Left
              </button>
              <button
                onClick={() => rotate('right')}
                className="flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-semibold py-3 rounded-lg transition-colors border border-zinc-200"
              >
                <RotateCw size={18} />
                Rotate Right
              </button>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleDownload}
                disabled={isProcessing}
                className="flex-1 bg-[#D4AF37] hover:bg-[#C19A2E] text-black font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-sm disabled:opacity-50"
              >
                {isProcessing ? <Loader2 className="animate-spin" size={20} /> : <Download size={20} />}
                Download Rotated {isPdf ? 'PDF' : 'Image'}
              </button>
              <button
                onClick={() => { setFile(null); setPreviewUrl(null); }}
                className="px-6 bg-zinc-50 hover:bg-zinc-100 text-zinc-500 font-medium rounded-lg transition-colors border border-zinc-200"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileRotator;